import { Button } from '@/design-system/button';
import { RadioGroup } from '@/design-system/radio-group';
import { Translation } from '@/hoslog-intl';
import { RadioGroupItem } from '@radix-ui/react-radio-group';
import { useEffect, useId, useState } from 'react';

type RangeProps = {
  items?: { key: string; value: number; className: string }[];
  value?: number;
  onRangeChange?: (range: number) => void;
  className?: string;
  row: { label: string; low: string; high: string };
  activeColor?: string;
  inactiveColor?: string;
  t?: (key: string) => string;
};

const colorConfig = [
  { key: 'Error/100', value: 1, className: 'bg-rating-active' },
  { key: 'Error/200', value: 2, className: 'bg-rating-active' },
  { key: 'Error/300', value: 3, className: 'bg-rating-active' },
  { key: 'Error/400', value: 4, className: 'bg-rating-active' },
  { key: 'Error/500', value: 5, className: 'bg-rating-active' },
];

export default function Range({ items = colorConfig, value, onRangeChange, className = '', row }: RangeProps) {
  const id = useId();

  const [currentRating, setCurrentRating] = useState(value ?? 3);

  useEffect(() => {
    if (typeof value !== 'number' || Number.isNaN(value)) return;
    setCurrentRating(value);
  }, [value]);

  const handleClick = (value: number) => {
    setCurrentRating(value);
    onRangeChange?.(value);
  };

  return (
    <fieldset className={`space-y-1 w-full ${className}`}>
      <legend className="text-foreground text-sm leading-[32px] select-none peer-disabled:cursor-not-allowed peer-disabled:opacity-50 font-bold">
        <Translation id={row.label} />
      </legend>
      <div className="relative w-full h-[20px]">
        <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 border-b-1 border-rating-border mx-1" />
        <RadioGroup
          className="relative z-2 flex h-full items-center justify-between"
          onValueChange={(value: string): void => {
            const newRating = Number(value);
            setCurrentRating(newRating);
            onRangeChange?.(newRating);
          }}
        >
          {items.map((item: { key: string; value: number; className: string }) => {
            const { key, value, className: itemClassName } = item;
            const isActive = currentRating === value;

            return (
              <div key={key} className="flex h-[20px] w-[20px] items-center justify-center">
                <Button
                  type="button"
                  className={`
                !rounded-full
                relative cursor-pointer
                transition-all duration-200 origin-center
                w-[12px] h-[12px] p-0
                flex items-center justify-center
                hover:${itemClassName}
                ${isActive ? itemClassName : 'bg-rating-inactive'}
              `}
                  onClick={(): void => handleClick(value)}
                >
                  <RadioGroupItem id={`${id}-${value}`} value={value.toString()} className="sr-only" />
                </Button>
              </div>
            );
          })}
        </RadioGroup>
      </div>
      <div className="flex justify-between text-caption text-rating-label leading-[18px]">
        <p>
          <Translation id={row.low} />
        </p>
        <p>
          <Translation id={row.high} />
        </p>
      </div>
    </fieldset>
  );
}
